import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const mapError = (detail) => {
    const m = {
      INVALID_TOKEN: language === 'hu' ? 'Érvénytelen vagy már felhasznált link.' : 'Invalid or already used link.',
      TOKEN_EXPIRED: language === 'hu' ? 'A link lejárt. Kérj újat.' : 'The link has expired. Please request a new one.',
      PASSWORD_TOO_SHORT: language === 'hu' ? 'A jelszónak legalább 6 karakternek kell lennie.' : 'Password must be at least 6 characters.',
    };
    return m[detail] || (language === 'hu' ? 'Hiba történt.' : 'Something went wrong.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError(language === 'hu' ? 'A két jelszó nem egyezik.' : 'Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/customer/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      setError(mapError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black py-16 px-4">
      <div className="container mx-auto max-w-md">
        <div className="bg-zinc-900 rounded-xl p-6 md:p-8 border border-zinc-800">
          <h1 className="text-2xl font-bold text-white mb-6">{language === 'hu' ? 'Új jelszó beállítása' : 'Set new password'}</h1>

          {!token ? (
            <p className="text-red-500 text-sm" data-testid="reset-no-token">
              {language === 'hu' ? 'Hiányzó vagy érvénytelen link.' : 'Missing or invalid link.'}
            </p>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-white mb-6">{language === 'hu' ? 'A jelszavad sikeresen megváltozott!' : 'Your password has been changed!'}</p>
              <Button className="bg-white text-black hover:bg-zinc-200 font-semibold" onClick={() => navigate('/account')} data-testid="reset-goto-login">
                {language === 'hu' ? 'Belépés' : 'Login'}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-zinc-300">{language === 'hu' ? 'Új jelszó' : 'New password'}</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700 text-white" required data-testid="reset-password-input" />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-zinc-300">{language === 'hu' ? 'Jelszó megerősítése' : 'Confirm password'}</Label>
                <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700 text-white" required data-testid="reset-confirm-input" />
              </div>
              {error && <p className="text-red-500 text-sm" data-testid="reset-error">{error}</p>}
              <Button type="submit" className="w-full bg-white text-black hover:bg-zinc-200 font-semibold" disabled={loading} data-testid="reset-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'hu' ? 'Jelszó mentése' : 'Save password')}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
